import React from 'react';
import { reportError } from '../utils/errorHandling.js';
import metrics from '../config/impactMetrics.js';

window.MissionPage = function MissionPage({ onBackClick, onDonateClick }) {
    try {
        // Animate elements on scroll
        React.useEffect(() => {
            const observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('animate-in');
                    }
                });
            }, { threshold: 0.1 });

            const elements = document.querySelectorAll('.animate-on-scroll');
            elements.forEach(el => observer.observe(el));

            return () => {
                elements.forEach(el => observer.unobserve(el));
            };
        }, []);

        return (
            <section data-name="mission-page" className="section bg-gray-900 text-white">
                <div className="container max-w-6xl mx-auto px-4 py-16 md:py-24">
                    {/* Back button */}
                    <button 
                        onClick={onBackClick}
                        className="mb-8 flex items-center text-green-400 hover:text-green-300 transition-colors"
                    >
                        <i className="fas fa-arrow-left mr-2"></i> Back to Home
                    </button>

                    {/* Hero Section */}
                    <div className="flex flex-col md:flex-row gap-12 items-center mb-24 animate-on-scroll">
                        <div className="md:w-1/2">
                            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white">
                                Our <span className="text-green-400">Mission</span>
                            </h1>
                            
                            <p className="text-xl md:text-2xl mb-8 text-gray-300 leading-relaxed">
                                Sustainable Compute Network is dedicated to transforming e-waste into high-performance 
                                computing solutions powered by 100% renewable energy.
                            </p>
                            
                            <div className="py-3 px-4 bg-green-500/20 border-l-4 border-green-500 rounded mb-8">
                                <p className="text-lg text-green-300">
                                    "Bridging the digital divide while fighting climate change through 
                                    innovative sustainable computing."
                                </p>
                            </div>
                            
                            <button onClick={onDonateClick} className="btn btn-primary transform hover:-translate-y-1 hover:shadow-xl text-lg px-8 py-4">
                                Donate Hardware
                                <i className="fas fa-arrow-right ml-2"></i>
                            </button>
                        </div>
                        
                        <div className="md:w-1/2 relative">
                            <div className="mission-image-container rounded-xl overflow-hidden shadow-2xl">
                                <img 
                                    src="https://images.unsplash.com/photo-1544027993-37dbfe43562a?crop=entropy&cs=srgb&fm=jpg&ixid=M3wzMjA0NjB8MHwxfHNlYXJjaHw0fHxzdXN0YWluYWJsZSUyMGNvbXB1dGluZ3xlbnwwfHx8fDE3NDI5NzA2NTl8MA&ixlib=rb-4.0.3&q=85" 
                                    alt="Sustainable Computing" 
                                    className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-green-900/60 to-transparent"></div>
                            </div>
                            
                            {/* Decorative elements */}
                            <div className="absolute -bottom-8 -right-8 w-40 h-40 border-4 border-green-500/30 rounded-xl -z-10"></div>
                            <div className="absolute -top-8 -left-8 w-24 h-24 bg-green-500/20 rounded-xl -z-10"></div>
                        </div>
                    </div>
                    
                    {/* Mission Pillars */}
                    <div className="mb-24">
                        <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center animate-on-scroll">
                            Our Three <span className="text-green-400">Pillars</span>
                        </h2>
                        
                        <div className="grid md:grid-cols-3 gap-8">
                            <div className="about-card animate-on-scroll">
                                <div className="bg-green-500/20 text-green-400 p-4 rounded-full w-16 h-16 flex items-center justify-center mb-6">
                                    <i className="fas fa-leaf text-2xl"></i>
                                </div>
                                <h3 className="text-2xl font-bold mb-4 text-white">Environmental Impact</h3>
                                <p className="text-gray-300">
                                    Reducing e-waste by giving new life to discarded hardware while operating on 100% renewable energy, 
                                    minimizing our carbon footprint and closing the technology lifecycle loop.
                                </p>
                            </div>
                            
                            <div className="about-card animate-on-scroll" style={{transitionDelay: '200ms'}}>
                                <div className="bg-green-500/20 text-green-400 p-4 rounded-full w-16 h-16 flex items-center justify-center mb-6">
                                    <i className="fas fa-globe-americas text-2xl"></i>
                                </div>
                                <h3 className="text-2xl font-bold mb-4 text-white">Digital Equity</h3>
                                <p className="text-gray-300">
                                    Providing affordable or free computing resources to underserved communities, schools,
                                    and research organizations that lack access to advanced computing infrastructure.
                                </p>
                            </div>
                            
                            <div className="about-card animate-on-scroll" style={{transitionDelay: '400ms'}}>
                                <div className="bg-green-500/20 text-green-400 p-4 rounded-full w-16 h-16 flex items-center justify-center mb-6">
                                    <i className="fas fa-lightbulb text-2xl"></i>
                                </div>
                                <h3 className="text-2xl font-bold mb-4 text-white">Innovation</h3>
                                <p className="text-gray-300">
                                    Developing cutting-edge technologies and methodologies for sustainable computing, 
                                    challenging the status quo of planned obsolescence in the tech industry.
                                </p>
                            </div>
                        </div>
                    </div>
                    
                    {/* Our Approach */}
                    <div className="mb-24 animate-on-scroll">
                        <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 md:p-12 shadow-2xl border border-gray-700">
                            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-white">Our <span className="text-green-400">Approach</span></h2>
                            
                            <div className="grid md:grid-cols-2 gap-12 items-center">
                                <div>
                                    <div className="mb-6">
                                        <h3 className="text-2xl font-bold mb-3 text-white flex items-center">
                                            <span className="bg-green-500/30 text-green-300 w-8 h-8 rounded-full flex items-center justify-center mr-3">1</span>
                                            Hardware Renewal
                                        </h3>
                                        <p className="text-gray-300 pl-11">
                                            We collect, refurbish, and optimize discarded hardware, giving it new purpose in our distributed computing network.
                                        </p>
                                    </div>
                                    
                                    <div className="mb-6">
                                        <h3 className="text-2xl font-bold mb-3 text-white flex items-center">
                                            <span className="bg-green-500/30 text-green-300 w-8 h-8 rounded-full flex items-center justify-center mr-3">2</span>
                                            Green Energy Integration
                                        </h3>
                                        <p className="text-gray-300 pl-11">
                                            Every device in our network runs on renewable energy sources, from solar and wind to innovative new solutions.
                                        </p>
                                    </div>
                                    
                                    <div>
                                        <h3 className="text-2xl font-bold mb-3 text-white flex items-center">
                                            <span className="bg-green-500/30 text-green-300 w-8 h-8 rounded-full flex items-center justify-center mr-3">3</span>
                                            Equitable Distribution
                                        </h3>
                                        <p className="text-gray-300 pl-11">
                                            We allocate computing resources to organizations that need them most, prioritizing impact and need.
                                        </p>
                                    </div>
                                </div>
                                
                                <div className="relative">
                                    <img 
                                        src="https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?crop=entropy&cs=srgb&fm=jpg&ixid=M3wzMjA0NjB8MHwxfHNlYXJjaHwyfHxzb2xhciUyMHBhbmVsc3xlbnwwfHx8fDE3NDI5NzA2NTl8MA&ixlib=rb-4.0.3&q=85" 
                                        alt="Sustainable Energy" 
                                        className="rounded-lg shadow-xl mb-4"
                                    />
                                    <img 
                                        src="https://images.unsplash.com/photo-1526666923127-b2970f64b422?crop=entropy&cs=srgb&fm=jpg&ixid=M3wzMjA0NjB8MHwxfHNlYXJjaHwyfHxjb21wdXRlciUyMGhhcmR3YXJlfGVufDB8fHx8MTc0Mjk3MDY1OXww&ixlib=rb-4.0.3&q=85" 
                                        alt="Computer Hardware" 
                                        className="rounded-lg shadow-xl absolute -bottom-8 -right-8 w-3/5 border-4 border-gray-800"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* Impact Statistics */}
                    <div className="mb-24 animate-on-scroll">
                        <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
                            Our <span className="text-green-400">Impact</span>
                        </h2>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
                            {metrics.map((metric, idx) => (
                                <div key={idx} className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 text-center border border-gray-700/50">
                                    <div className="text-green-400 text-4xl md:text-5xl font-bold mb-2">{metric.value}</div>
                                    <p className="text-gray-300">{metric.label}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                    
                    {/* Call to Action */}
                    <div className="text-center animate-on-scroll">
                        <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
                            Join Our <span className="text-green-400">Mission</span>
                        </h2>
                        <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
                            Whether you're donating hardware, providing renewable energy resources, 
                            or applying for computing power, there's a place for you in our network.
                        </p>
                        <div className="flex flex-wrap gap-4 justify-center">
                            <button onClick={onDonateClick} className="btn btn-primary transform hover:-translate-y-1 hover:shadow-xl text-lg px-8 py-4">
                                Donate Hardware
                                <i className="fas fa-arrow-right ml-2"></i>
                            </button>
                            <button className="btn btn-secondary transform hover:-translate-y-1 hover:shadow-xl text-lg px-8 py-4">
                                Volunteer With Us
                                <i className="fas fa-users ml-2"></i>
                            </button>
                        </div>
                    </div>
                </div>
                
                {/* Wave divider */}
                <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none">
                    <svg className="relative block w-full h-24" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120">
                        <path d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z" opacity=".25" className="fill-gray-800"></path>
                        <path d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z" opacity=".5" className="fill-gray-800"></path>
                        <path d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z" className="fill-gray-800"></path>
                    </svg>
                </div>
            </section>
        );
    } catch (error) {
        console.error('MissionPage error:', error);
        reportError(error);
        return null;
    }
}

export default MissionPage;
